import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import {
  Box,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  stepConnectorClasses,
  stepLabelClasses,
  styled,
  Typography,
} from "@mui/material";
import type { Instant } from "adl-gen/common";
import type {
  RevaluationRequestState,
  RevaluationRequestStateEvent,
} from "adl-gen/ferovinum/app/db";
import { useCallback } from "react";
import { formatInstant } from "utils/date-utils";
import { assertNever } from "utils/hx/util/types";

export interface RevaluationStepperProps {
  stateEvents: RevaluationRequestStateEvent[];
}

export const RevaluationStepper = ({
  stateEvents,
}: RevaluationStepperProps) => {
  const stepsToDisplay = getDisplayStates(stateEvents);

  const checkStepOccured = useCallback((time: Instant): boolean => {
    const now = Date.now();
    return time < now;
  }, []);
  return (
    <Stepper
      orientation="vertical"
      activeStep={stateEvents.length}
      connector={<StepperConnector />}
    >
      {stepsToDisplay.map((step, index) => {
        const { state, time } = step;
        const description = getStateDescription(state);
        return (
          <Step key={index}>
            <StepLabel
              icon={
                checkStepOccured(time) ? undefined : (
                  <Box color="common.grey">
                    <CircleOutlinedIcon color="inherit" />
                  </Box>
                )
              }
              sx={{ [`& .${stepLabelClasses.labelContainer}`]: { ml: 4 } }}
              optional={
                checkStepOccured(time) ? formatInstant(time) : undefined
              }
            >
              <Typography>{description}</Typography>
            </StepLabel>
          </Step>
        );
      })}
    </Stepper>
  );
};

const getDisplayStates = (
  stateEvents: RevaluationRequestStateEvent[],
): RevaluationRequestStateEvent[] => {
  const currentState = stateEvents[stateEvents.length - 1].state;
  switch (currentState) {
    case "created": {
      const futureTime = new Date().setFullYear(new Date().getFullYear() + 1);
      const newEvent: RevaluationRequestStateEvent = {
        time: futureTime, // Set futureTime for events that are not yet triggered but needs to be displayed
        state: "orgAccepted",
      };
      return [...stateEvents, newEvent];
    }

    default:
      return stateEvents;
  }
};

function getStateDescription(state: RevaluationRequestState) {
  switch (state) {
    case "created":
      return "Revaluation request ready for review";
    case "orgAccepted":
      return "Revaluation request accepted";
    case "orgRejected":
      return "Revaluation request rejected";
    default:
      return assertNever(state);
  }
}

const StepperConnector = styled(StepConnector)(() => ({
  [`& .${stepConnectorClasses.line}`]: { borderLeftStyle: "dashed" },
}));
